import React from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { withRouter } from "react-router";
import { compose } from "ramda";
import { Link, Route } from "react-router-dom";
import cx from "classnames";
import PriorityPlusNav from "@cxagroup/react-priority-plus-nav";
import messages from "components/messagesDescription";
import { intlShape, injectIntl } from "react-intl";
import { isExist, getUrlWithQuery } from "cxa-utils";
import { RouteMap } from "./routeMap";
import { showPopup } from "actions/popupActions";
import { markDisclaimerShown } from "actions/marketplaceActions";
import { renderAsHTML } from "../../../utils";
import SecondaryNavPP from "../navigation/SecondaryNavPP";
import withMatchMediaProps from "@hocs/with-match-media-props";

const isValidMainMenuItem = ({ id, children, externalUrl }) => {
  const routeName = RouteMap[id];

  if (isExist(children)) {
    const isInvalid = children.some(childItem => !RouteMap[childItem.id]);

    if (isInvalid) {
      console.warn(`No matching routes found for menu ${id}`);
    }

    return isInvalid;
  }

  return externalUrl ? !routeName : !!routeName;
};

const isBenefitUrl = (newUIFlag, topNavUrl) => {
  const { pathname } = window.location;

  // List of valid pathnames for benefits
  const benefitPathnames = ["/shop/enrolment", "/shop/checkout", "/shop/checkout/success"];

  // Check if currentPathname is a benefit URL and topNavUrl is "/benefits"
  return benefitPathnames.includes(pathname) && topNavUrl === "/benefits" && newUIFlag;
};

const NavMenus = ({ parents, intl, selectedLanguage, history, isNarrowScreen }) => {
  const clientSettings = useSelector(state => state.common.clientSettings);
  const marketplace = useSelector(state => state.marketplace);
  const dashboardActionsData = useSelector(state => state.dashboardPP.dashboardActions);

  const newUIFlag = clientSettings.newUIFlag;
  const subMenus = useSelector(state => state.common.menuItems);

  const { shopDisclaimerNote } = clientSettings;
  const dispatch = useDispatch();

  const handleClickShop = evt => {
    evt.preventDefault();
    const { currentTarget } = evt;
    const href = currentTarget.getAttribute("href");
    dispatch(
      showPopup({
        element: renderAsHTML(
          "div",
          intl.formatMessage({ id: "config.shopDisclaimer", defaultMessage: shopDisclaimerNote })
        ),
        callback: () => {
          href === "/marketplace" ? history.push(href) : (window.location.href = href);
          dispatch(markDisclaimerShown());
        },
        showClose: true,
      })
    );
  };

  const menuItems = parents.reduce((acc, item) => {
    const { id, name, externalUrl } = item;

    const isShopLink = id.toLowerCase() === "cxashopmain_cxashop";
    const label = intl.formatMessage({ id: name, defaultMessage: name });
    let menu = {
      key: id,
      to: RouteMap[id],
      label,
      externalUrl,
    };

    if (!isValidMainMenuItem(item)) {
      console.warn({
        key: id,
        to: "/404",
        label: `Broken menu configuration ${id}`,
      });
    }

    if (isShopLink) {
      const onClick =
        isExist(shopDisclaimerNote) && marketplace.showDisclaimer ? handleClickShop : () => {};
      const { code: langCode = "" } = selectedLanguage;
      menu = {
        ...menu,
        onClick,
        externalUrl: externalUrl
          ? getUrlWithQuery(externalUrl, { langid: langCode.replace("-", "") })
          : null,
      };
    }

    return [...acc, menu];
  }, []);

  menuItems.push({
    key: "needhelp",
    to: "/needHelp",
    label: intl.formatMessage(messages.helpMenuDescription),
    externalUrl: null,
  });

  const renderRoutedMenuItem = ({ itemDetails, captureRef, clickHandler, activeClass }) => {
    const { key, to, label, externalUrl, onClick = () => {} } = itemDetails;
    const isShopLink = key.toLowerCase() === "cxashopmain_cxashop";

    const extractedWords = dashboardActionsData && Object.keys(dashboardActionsData);

    const isTabRelevant = (to, word) => {
      const lowerCasedTo = to
        .trim()
        .toLowerCase()
        .replace(/\//g, "");
      const lowerCasedWord = word.trim().toLowerCase();
      return lowerCasedWord.includes(lowerCasedTo);
    };

    return (
      <Route key={label} path={externalUrl || to}>
        {({ match }) => {
          const isActive = match || location.pathname === (externalUrl || to);
          const cls = cx(
            { [activeClass]: isExist(match) || isBenefitUrl(newUIFlag, to) },
            "navbar-label-pp"
          );
          const link = isExist(externalUrl) ? (
            <a href={externalUrl} onClick={onClick}>
              {label}
            </a>
          ) : isShopLink ? (
            <Link to={to} onClick={onClick}>
              {label}
            </Link>
          ) : (
            <Link to={to}>
              <div>
                {label}

                {newUIFlag && (
                  <div>
                    {isActive && isNarrowScreen && <SecondaryNavPP items={subMenus} />}
                    {extractedWords && (
                      <span
                        className={`navbar-label-icon-pp${
                          extractedWords.some(
                            word => isTabRelevant(to, word) && dashboardActionsData[word].length
                          )
                            ? " with-background"
                            : ""
                        }`}
                      >
                        {extractedWords.map(word =>
                          isTabRelevant(to, word) ? dashboardActionsData[word].length : ""
                        )}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </Link>
          );

          return (
            <li
              key={key}
              className={cls}
              ref={captureRef}
              style={newUIFlag ? { padding: "10px 20px", marginLeft: "0px" } : {}}
            >
              <button onClick={clickHandler} className="link">
                {link}
              </button>
            </li>
          );
        }}
      </Route>
    );
  };

  return (
    <PriorityPlusNav
      menuItems={menuItems}
      renderMenuItem={renderRoutedMenuItem}
      menuText={intl.formatMessage(messages.menuTextDescription)}
      moreText={intl.formatMessage(messages.moreTextDescription)}
      className="apl-nav apl-nav--primary"
    />
  );
};

NavMenus.propTypes = {
  intl: intlShape.isRequired,
  parents: PropTypes.array,
  clientSettings: PropTypes.object,
  selectedLanguage: PropTypes.object,
  history: PropTypes.object,
  marketplace: PropTypes.object,
};

NavMenus.defaultProps = {
  selectedLanguage: {},
};

export default compose(
  withRouter,
  injectIntl
)(
  withMatchMediaProps({
    isNarrowScreen: {
      maxWidth: 680,
    },
  })(NavMenus)
);



// WEBPACK FOOTER //
// ./components/common/navigation/NavMenus.js